import {
  Form,
  Loading,
  SaveButton,
  useGetOne,
  useInput,
  useRedirect,
  useTranslate
} from 'react-admin'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Box, Button, Grid, Typography } from '@mui/material'
import React, { useState } from 'react'
import { Calendar, DateObject, type Value } from 'react-multi-date-picker'
import 'react-multi-date-picker/styles/layouts/mobile.css'
import log from 'loglevel'
import {
  concat,
  find,
  isEmpty,
  isNil,
  map,
  pull,
  split,
  template,
  toNumber
} from 'lodash/fp'
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  format,
  lastDayOfMonth
} from 'date-fns/fp'
import { useFormContext, useFormState } from 'react-hook-form'
import axios from 'axios'
import { assign } from 'lodash'
import { HeaderLogo, setTitle } from '../Util'

const schema = z.object({
  schedules: z.array(z.string())
})
const env = process.env['REACT_APP_ENV']
const urls = {
  prd: 'https://api-b2b-v2.terashite.jp',
  stg: 'https://api-b2b-v2-stg.terashite.jp',
  int: 'https://api-b2b-v2-int.dev.terashite.jp',
  dev: 'https://api-b2b-v2.dev.terashite.jp',
  exp: 'https://api-b2b-v2-exp.dev.terashite.jp'
}
const url = urls[env as keyof typeof urls]

const FormContent: React.FC<{
  id: string
  notification: string
  edit: boolean
  maxDays?: string | undefined
}> = ({ id, notification, edit, maxDays, ...props }) => {
  const translate = useTranslate()
  const today = new Date()
  const { getValues } = useFormContext()
  const { ...data } = getValues()
  const schedules = data['schedules']
  const partner = data['partner']
  const [values, setValues] = React.useState<Value[][]>(
    !isNil(schedules)
      ? map((item: string) => [item, item])(schedules)
      : [[today]]
  )
  const [currentDate, setCurrentDate] = React.useState<DateObject>(
    new DateObject(today)
  )
  const dateString = (item: Value | undefined): string =>
    item instanceof Date || item instanceof DateObject
      ? format('yyyy-MM-dd')(item instanceof Date ? item : item.toDate())
      : isNil(item)
      ? 'undefined'
      : item.toString()
  const dateArrayString = (
    items: Value[][] | Value[] | undefined
  ): string[][] =>
    isNil(items)
      ? [['undefined']]
      : map((item: Value[] | Value) =>
          map((subItem: Value) => dateString(subItem))(
            item instanceof Array ? item : [item]
          )
        )(items)
  const ScheduleInput: React.FC<{
    source: string
    maxDays?: string | undefined
  }> = ({ source, maxDays, ...props }) => {
    const jaJp = {
      name: 'ja_jp',
      months: [
        ['1月', '1月'],
        ['2月', '2月'],
        ['3月', '3月'],
        ['4月', '4月'],
        ['5月', '5月'],
        ['6月', '6月'],
        ['7月', '7月'],
        ['8月', '8月'],
        ['9月', '9月'],
        ['10月', '10月'],
        ['11月', '11月'],
        ['12月', '21月']
      ],
      weekDays: [
        ['土曜日', '土'],
        ['日曜日', '日'],
        ['月曜日', '月'],
        ['火曜日', '火'],
        ['水曜日', '水'],
        ['木曜日', '木'],
        ['金曜日', '金']
      ],
      digits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
      meridiems: [
        ['午前', '午前'],
        ['午後', '午後']
      ]
    }
    const { setValue } = useFormContext()
    const { field } = useInput({ source, ...props })
    const setFormValue = (values: DateObject[][]): void => {
      setValues(values)
      const value = map((item: DateObject[]) => dateString(item[0]))(values)
      setValue(source, value, { shouldDirty: true, shouldValidate: true })
      field.onChange({ target: { value } })
    }
    const onChange = (newValues: DateObject[][]): void => {
      const valuesString = dateArrayString(values)
      if (
        valuesString.toString() ===
        [dateString(today), dateString(today)].toString()
      ) {
        const value = find(
          (item: Value) =>
            (item instanceof Array
              ? dateArrayString(item)
              : dateString(item)
            ).toString() !== valuesString.toString()
        )(newValues)
        if (
          !isNil(value) &&
          value instanceof Array &&
          value[0] instanceof DateObject
        ) {
          const adjust = map((item: Date) => [
            new DateObject(item),
            new DateObject(item)
          ])(eachDayOfInterval({ start: today, end: value[0].toDate() }))
          setValues(adjust)
          setFormValue(adjust)
          return
        }
      }
      let adjust: DateObject[][] = []
      for (const value of newValues) {
        if (
          value instanceof Array &&
          value.length > 1 &&
          value[0] instanceof DateObject &&
          value[1] instanceof DateObject
        ) {
          if (dateString(value[0]) !== dateString(value[1])) {
            if (dateString(value[0]) === dateString(today)) {
              const flat = map((item: Date) => [
                new DateObject(item),
                new DateObject(item)
              ])(
                eachDayOfInterval({
                  start: value[0].toDate(),
                  end: value[1].toDate()
                })
              )
              adjust = concat(flat)(adjust)
            } else {
              adjust.push([value[0], value[0]])
              adjust.push([value[1], value[1]])
            }
          } else {
            adjust.push(value)
          }
        } else {
          if (!isNil(value) && !isNil(value[0])) {
            adjust.push([value[0], value[0]])
          }
        }
      }
      setFormValue(adjust)
    }
    const onFocusedDateChange = (
      // @ts-expect-error ts(6133)
      focused: DateObject,
      clicked: DateObject
    ): void => {
      setCurrentDate(clicked)
      if (
        dateString(clicked) === dateString(today) &&
        dateArrayString(values).toString() !== [dateString(today)].toString()
      ) {
        setValues(values)
        setFormValue(values as DateObject[][])
        return
      }
      const deleted = find(
        (item: DateObject[] | Date) =>
          dateString(clicked) ===
          dateString(item instanceof Array ? item[0] : item)
      )(values)
      if (!isNil(deleted) && !isEmpty(deleted)) {
        setFormValue(pull(deleted)(values as DateObject[][]) as DateObject[][])
      }
    }
    return (
      <Calendar
        multiple
        range
        value={values}
        currentDate={currentDate}
        minDate={today}
        maxDate={
          !isNil(maxDays)
            ? addDays(toNumber(maxDays) - 1)(today)
            : lastDayOfMonth(addMonths(1)(today))
        }
        onChange={onChange}
        onFocusedDateChange={onFocusedDateChange}
        className="rmdp-mobile"
        style={{
          transform: 'scale(1.5, 1.5)',
          marginTop: '3em',
          marginBottom: '3em',
          fontWeight: 'bold'
        }}
        mapDays={() => {
          const props = {
            style: {
              fontSize: '18px',
              fontWeight: 'bold'
            }
          }
          return props
        }}
        locale={navigator.language === 'ja' ? jaJp : undefined}
        monthYearSeparator=" "
        // @ts-expect-error
        formatMonth={(month: string, year: string) => {
          return `${year}年`
        }}
        // @ts-expect-error
        formatYear={(year: string, month: stirng) => {
          return month
        }}
        disableYearPicker
        disableMonthPicker
      />
    )
  }
  const ClearButton: React.FC<{
    source: string
  }> = ({ source, ...props }) => {
    const { setValue } = useFormContext()
    const { field } = useInput({ source, ...props })
    const onClick = (): void => {
      setValues([[today]])
      setCurrentDate(new DateObject(today))
      setValue(source, [today], { shouldDirty: true, shouldValidate: true })
      field.onChange({ target: { values: [today] } })
    }
    return (
      <Button
        variant="contained"
        size="small"
        onClick={onClick}
        sx={{
          borderRadius: '20px',
          backgroundColor: '#4472c4'
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('resources.schedule.clear')}
        </Typography>
      </Button>
    )
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box width="100%">
        <Box sx={{ marginLeft: '0.5em' }}>
          <HeaderLogo partner={partner} />
        </Box>
        <Box sx={{ backgroundColor: '#fff7e1' }}>
          <Grid
            container
            justifyContent="center"
            spacing={2}
            sx={{
              marginTop: '0.1em'
            }}>
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: '#f2ecda' }}>
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginTop: '0.5em',
                      marginBottom: '0.5em',
                      marginLeft: '0.5em'
                    }}>
                    {translate('resources.schedule.title')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginTop: '0.5em',
                      marginBottom: '0.5em',
                      marginLeft: '0.5em',
                      marginRight: '0.5em'
                    }}>
                    {template(translate('resources.schedule.header'))({
                      end: isNil(maxDays)
                        ? '来月末'
                        : format('M月d日')(
                            addDays(toNumber(maxDays) - 1)(today)
                          )
                    })}
                  </Typography>
                </Grid>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '3em',
                marginTop: '2em'
              }}>
              <ScheduleInput source="schedules" maxDays={maxDays} />
            </Grid>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em',
                marginLeft: '0.5em'
              }}>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '2.5em'
                }}>
                <ClearButton source="schedules" />
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '2.5em'
                }}>
                <CancelButton id={id} notification={notification} />
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '2.5em'
                }}>
                <SubmitButton
                  {...props}
                  id={id}
                  notification={notification}
                  edit={edit}
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}
const SubmitButton: React.FC<{
  id: string
  notification: string
  edit?: boolean
}> = ({ id, notification, edit, ...props }) => {
  const [enable, setEnable] = useState(false)
  const { getValues } = useFormContext()
  const { isValid } = useFormState()
  const translate = useTranslate()
  const redirect = useRedirect()
  const onSubmit = (): void => {
    const { ...data } = getValues()
    setEnable(false)
    const input = { ...data, id, notification }
    if (isNil(input['schedules' as keyof typeof input])) {
      assign(input, { schedules: [] })
    }
    axios({
      url: `${url}/partner-user-public/v1/schedule`,
      method: 'POST',
      data: input
    })
      .then(() => {
        redirect(`/schedule/${id}/${notification}/complete`)
      })
      .catch(log.error)
  }
  React.useEffect(() => {
    setEnable(isValid || (!isValid && !isNil(edit) && edit))
  }, [edit, isValid, getValues])
  return (
    <SaveButton
      {...props}
      icon={<></>}
      variant="contained"
      size="large"
      disabled={!enable}
      sx={{
        borderRadius: '20px',
        backgroundColor: '#4472c4'
      }}
      // @ts-expect-error
      label={
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {translate('resources.schedule.submit.l1')}
        </Typography>
      }
      onClick={onSubmit}
    />
  )
}
const CancelButton: React.FC<{
  id: string
  notification: string
}> = ({ id, notification }) => {
  const translate = useTranslate()
  const redirect = useRedirect()
  const onClick = (): void => {
    redirect(`/schedule/${id}/${notification}/complete`)
  }
  return (
    <Button
      variant="contained"
      size="small"
      onClick={onClick}
      sx={{
        borderRadius: '20px',
        backgroundColor: '#4472c4'
      }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {translate('resources.schedule.cancel')}
      </Typography>
    </Button>
  )
}
export const ScheduleForm: React.FC<{
  id: string
  notification: string
  maxDays?: string | undefined
}> = ({ id, notification, maxDays, ...props }) => {
  const { data, isLoading } = useGetOne('users', { id })
  const translate = useTranslate()
  React.useEffect(() => {
    if (isLoading) return
    const { partner } = data
    setTitle(partner, translate)
  }, [isLoading, data, translate])
  if (isLoading) return <Loading />
  return (
    <Form
      defaultValues={data}
      resolver={zodResolver(schema)}
      mode="onBlur"
      reValidateMode="onBlur">
      <FormContent
        {...props}
        id={id}
        notification={notification}
        edit={!isNil(data.schedules) && !isEmpty(data.schedules)}
        maxDays={maxDays}
      />
    </Form>
  )
}
export const ScheduleCompleteForm: React.FC<{
  id: string
}> = ({ id }) => {
  const translate = useTranslate()
  // const { data, isLoading } = useGetOne('notifications', {
  //   id: notification
  // })
  // React.useEffect(() => {
  //   if (isLoading) return
  //   const { partner } = data
  //   setTitle(partner, translate)
  // }, [isLoading, data, translate])
  // if (isLoading) return <Loading />
  const { data, isLoading } = useGetOne('users', { id })
  React.useEffect(() => {
    if (isLoading) return
    const { partner } = data
    setTitle(partner, translate)
  }, [isLoading, data, translate])
  if (isLoading) return <Loading />
  const { partner } = data ?? { partner: null }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <HeaderLogo partner={partner} />
        </Box>
        <Box
          sx={{
            backgroundColor: '#fff7e1',
            paddingTop: '2em',
            paddingBottom: '3em'
          }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              {id === '0' && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginTop: '0em',
                      marginBottom: '1em'
                    }}>
                    {id === '0'
                      ? translate('resources.schedule.title')
                      : translate('resources.schedule.thanks')}
                  </Typography>
                </Grid>
              )}
              {id !== '0' &&
                map((text: string) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Typography variant="h6">{text}</Typography>
                    </Grid>
                  )
                })(
                  split('\n')(`${translate('resources.schedule.thanksFooter')}`)
                )}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
